import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlanTypes } from '../store/planTypeSlice';
import { createPlan, fetchPlans } from '../store/planSlice';
import PlanList from '../components/PlanList';

const PlanDetailScreen = () => {
  const dispatch = useDispatch();
  const { planTypes } = useSelector((state) => state.planType);
  const { plans } = useSelector((state) => state.plans);

  const [form, setForm] = useState({
    plan: {
      name: '',
      description: '',
      tnc: '',
      faqlink: '',
      plantypeId: '',
      isactive: true,
      isbtc: true,
    },
    planDetail: {
      days: '',
      price: '',
      monthlyYearly: null, // No field needed
      cancellationDays: '',
      perDayPrice: '',
      actualPrice: '',
    },
  });

  useEffect(() => {
    dispatch(fetchPlanTypes());
    dispatch(fetchPlans());
  }, [dispatch]);

  const handleChange = (section, name, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value,
      },
    }));
  };

  const handleCheckbox = (section, name) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: !prev[section][name],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const cleanedPayload = {
      plan: { ...form.plan },
      planDetail: {
        ...form.planDetail,
        days: Number(form.planDetail.days),
        price: Number(form.planDetail.price),
        perDayPrice: Number(form.planDetail.perDayPrice),
        actualPrice: Number(form.planDetail.actualPrice),
        cancellationDays: Number(form.planDetail.cancellationDays),
        monthlyYearly: form.planDetail.monthlyYearly || null,
      },
    };
  
    console.log('📦 Posting Plan Payload:', JSON.stringify(cleanedPayload, null, 2));
  
    const resultAction = await dispatch(createPlan(cleanedPayload));
  
    if (createPlan.fulfilled.match(resultAction)) {
      dispatch(fetchPlans()); // ✅ Refresh the list after create
      setForm({
        plan: {
          name: '',
          description: '',
          tnc: '',
          faqlink: '',
          plantypeId: '',
          isactive: true,
          isbtc: true,
        },
        planDetail: {
          days: '',
          price: '',
          monthlyYearly: null,
          cancellationDays: '',
          perDayPrice: '',
          actualPrice: '',
        },
      });
    } else {
      console.error("❌ Failed to create plan", resultAction.payload || resultAction.error);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <h2 className="text-2xl font-semibold">Add Plan + Plan Detail</h2>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded shadow">
        {/* Plan Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Plan Name</label>
            <input
              type="text"
              name="name"
              value={form.plan.name}
              onChange={(e) => handleChange('plan', 'name', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">FAQ Link</label>
            <input
              type="text"
              name="faqlink"
              value={form.plan.faqlink}
              onChange={(e) => handleChange('plan', 'faqlink', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium">Description</label>
            <textarea
              name="description"
              value={form.plan.description}
              onChange={(e) => handleChange('plan', 'description', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium">Terms & Conditions</label>
            <textarea
              name="tnc"
              value={form.plan.tnc}
              onChange={(e) => handleChange('plan', 'tnc', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Plan Type</label>
            <select
              name="plantypeId"
              value={form.plan.plantypeId}
              onChange={(e) => handleChange('plan', 'plantypeId', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select a Type</option>
              {planTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.plan_type}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm">
              <input
                type="checkbox"
                checked={form.plan.isactive}
                onChange={() => handleCheckbox('plan', 'isactive')}
                className="mr-2"
              />
              Active
            </label>
            <label className="text-sm">
              <input
                type="checkbox"
                checked={form.plan.isbtc}
                onChange={() => handleCheckbox('plan', 'isbtc')}
                className="mr-2"
              />
              B2C (Pre-selected)
            </label>
          </div>
        </div>

        {/* Plan Detail Fields */}
        <div className="border-t pt-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Days</label>
            <input
              type="number"
              name="days"
              value={form.planDetail.days}
              onChange={(e) => handleChange('planDetail', 'days', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Price</label>
            <input
              type="number"
              name="price"
              value={form.planDetail.price}
              onChange={(e) => handleChange('planDetail', 'price', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Per Day Price</label>
            <input
              type="number"
              name="perDayPrice"
              value={form.planDetail.perDayPrice}
              onChange={(e) => handleChange('planDetail', 'perDayPrice', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Actual Price</label>
            <input
              type="number"
              name="actualPrice"
              value={form.planDetail.actualPrice}
              onChange={(e) => handleChange('planDetail', 'actualPrice', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Cancellation Days</label>
            <input
              type="number"
              name="cancellationDays"
              value={form.planDetail.cancellationDays}
              onChange={(e) => handleChange('planDetail', 'cancellationDays', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Save Plan
        </button>
      </form>

      <PlanList plans={plans} />
    </div>
  );
};

export default PlanDetailScreen;
